<template>
  <MasterDetail
    :emptyStateMessage="$t(`empty_states.${scope}.message`)"
    :emptyStateTitle="$t('empty_states.no_inspections')"
    :items="inspections"
    :loading="loading"
    :pageTitle="$t('nav.inspections')"
    :searchKeys="['inspected_by.full_name', 'id', 'date']"
    :searchPlaceholder="$t('plants.search_inspections')"
    :showDetail="!isIndex"
    :sortByOptions="sortByOptions(I18n)"
    :tabs="tabs"
    @newInspection="newInspectionModal()"
  >
    <template #mobileActions>
      <MobileNewInspection
        v-if="currentShaft && currentShaft.policy.inspect"
        @click="newInspectionModal"
      />
    </template>

    <template #item="{ item }">
      <InspectionCard
        :inspection="item"
        :pathName="isShaftV2 ? 'shaftV2Inspection' : 'shaftInspection'"
      />
    </template>

    <template #actions>
      <NewInspection
        v-if="currentShaft && currentShaft.policy.inspect"
        @open="newInspectionModal"
      />
    </template>

    <template #detail>
      <router-view
        @deleteInspection="deleteInspection"
        @statusChange="updateStatus"
        @updateInspection="updateInspection"
      />
    </template>
  </MasterDetail>
  <ShaftInspectionForm
    v-model:modalIsOpen="modalIsOpen"
    :initialInspection="formInitialInspection"
    @save="createInspection"
  />
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import I18n from 'i18n-js';

// Utils
import useAjax from '@/functions/use_ajax';
import { useShaft, useShaftV2 } from '@/functions/use_remote_object';
import { sortByOptions } from '@/functions/inspections';

// Components
import MasterDetail from '@/components/generic/master_detail.vue';
import InspectionCard from '@/components/shafts/inspections/inspection_card.vue';
import ShaftInspectionForm from '@/components/inspections/inspection_form.vue';
import NewInspection from '@/components/inspections/new_inspection_action.vue';
import MobileNewInspection from '@/components/inspections/new_inspection_mobile_action.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
  inspectionId: { type: String, required: false, default: null },
  scope: { type: String, default: 'inspections' },
});

const newInspection = {
  date: new Date(),
  trusted_user: false
};

const newV2Inspection = {
  date: new Date(),
  inspection_type: 'general',
  professional_engineer: false,
  trusted_user: false
};

const formInitialInspection = computed(() => {
  return isShaftV2.value
    ? newV2Inspection
    : newInspection;
});

const ajax = useAjax();

const tabs = computed(() => [
  {
    count: provisionalInspectionsCount.value,
    label: I18n.t('inspections.provisional_count'),
    to: { name: inspectionRoute.value.name, params: { scope: 'inspections' } },
  },
  {
    count: approvedInspectionsCount.value,
    label: I18n.t('inspections.approved_count'),
    to: { name: inspectionRoute.value.name, params: { scope: 'approved_inspections' } },
  },
]);

const inspections = ref([]);
const loading = ref(true);
const modalIsOpen = ref(false);
const route = useRoute();
const router = useRouter();
const provisionalInspectionsCount = ref(0);
const approvedInspectionsCount = ref(0);

const isShaftV2 = computed(() => route.path.includes('shafts_v2'));
const currentShaft = isShaftV2.value ? useShaftV2(props) : useShaft(props);

const isIndex = computed(() => {
  return (route.name === 'shaftV2Inspections' || route.name === 'shaftInspections');
});

onMounted(() => {
  loadInspections();
});

watch(() => props.scope, (value, oldValue) => {
  if (inspections.value && value === oldValue) return;
  loadInspections();
  }
);

const inspectionRoute = computed(() => {
  return {
    name: isShaftV2.value ? 'shaftV2Inspections' : 'shaftInspections',
    url: isShaftV2.value ? 'shafts_v2/shafts' : 'shafts',
  };
});

const ENDPOINTS = computed(() => {
  return {
    inspections: `/organisations/${props.organisationId}/${inspectionRoute.value.url}/${props.shaftId}/${props.scope}`,
  };
});

async function loadInspections() {
  loading.value = true;

  let { ok, data } = await ajax(ENDPOINTS.value.inspections);

  if (ok) {
    inspections.value = data.inspections;
    provisionalInspectionsCount.value = data.inspections_count;
    approvedInspectionsCount.value = data.approved_count;
  }
  loading.value = false;
}

/**
 * Displays the new inspection modal
 */
function newInspectionModal() {
  formInitialInspection.value.trusted_user = currentShaft.value?.trusted_user;
  modalIsOpen.value = true;
}

/**
 * Function for an emitted event from inspection details.
 *  updates the status.
 */
function updateStatus({ id, status }) {
  let inspection = inspections.value.find((i) => i.id === id);
  let scope = props.scope;

  if (inspection.status === 'approved' && status !== 'approved') {
    provisionalInspectionsCount.value++;
    approvedInspectionsCount.value--;
    scope = 'inspections';
  } else if (inspection.status !== 'approved' && status === 'approved') {
    provisionalInspectionsCount.value--;
    approvedInspectionsCount.value++;
    scope = 'approved_inspections';
  }

  inspection.status = status;

  router.replace({ params: { scope } });
}

/**
 * Function for an emitted event from inspection details.
 *  updates the inspection.
 */
function updateInspection(inspection) {
  inspections.value = inspections.value.map(i => {
    if (i.id === inspection.id) {
      return inspection;
    } else {
      return i;
    }
  });
}

/**
 * Function for an emitted event from inspection details.
 *  deletes an inspection.
 */
function deleteInspection() {
  loadInspections();
}

/**
 * Submit new inspection
 */
async function createInspection(inspection) {
  provisionalInspectionsCount.value++;
  inspections.value = [...inspections.value, inspection];

  router.push({
    name: isShaftV2.value ? 'shaftV2Inspection' : 'shaftInspection',
    params: { inspectionId: inspection.id, scope: 'inspections' },
  });
}
</script>
